import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Stack, Typography } from '@mui/material';
import useAppTheme from '../../../hooks/useAppTheme';
import scatterplotDark from '../../../assets/images/scatterplot-dark.png';
import scatterplotLight from '../../../assets/images/scatterplot-light.png';
import agingWIP from '../../../assets/images/aging-wip.png';
import monteCarloWhenDark from '../../../assets/images/mc-when-dark.png';
import monteCarloWhenLight from '../../../assets/images/mc-when-light.png';
import { boldText } from '../../../i18n/formattedMessageXMLTags';
import { LandingPageTitle } from '../style';
import {
  FeaturesHighlightColumn,
  FeaturesHighlightImage,
  FeaturesHighlights,
  FeaturesHighlightTitle,
  FeaturesOverviewSection,
} from './style';

export default function FeaturesOverview() {
  const { isMobile, isLaptop, isWideScreen, isDarkTheme } = useAppTheme();
  return (
    <FeaturesOverviewSection ismobile={isMobile} islaptop={isLaptop} iswidescreen={isWideScreen}>
      <LandingPageTitle variant="h5">
        <FormattedMessage
          id="landing.page.features.overview.title"
          defaultMessage="Begin uncovering insights in your data"
        />
      </LandingPageTitle>
      <Typography variant="body1" color="textPrimary">
        <FormattedMessage
          id="landing.page.features.overview.introduction"
          defaultMessage="Once you load a data set, you’ll start to see how work flows through your process.
          This section introduces key ways to work with your data. Whether you're looking back, staying on track,
          or planning ahead, see which charts to use to kick off your exploration."
        />
      </Typography>

      <FeaturesHighlights ismobile={isMobile} islaptop={isLaptop}>
        <FeaturesHighlightColumn>
          <Stack spacing={2}>
            <FeaturesHighlightImage
              src={isDarkTheme ? scatterplotDark : scatterplotLight}
              alt="Cycle Time Scatterplot"
              loading="lazy"
            />
            <FeaturesHighlightTitle variant="subtitle1" color="textPrimary">
              <FormattedMessage
                id="landing.page.features.overview.learn.title"
                defaultMessage="Learn from the past"
              />
            </FeaturesHighlightTitle>
            <Typography variant="body2" color="textPrimary">
              <FormattedMessage
                id="landing.page.features.overview.learn.cycle.time.scatterplot"
                defaultMessage='Use the <b>Cycle Time Scatterplot</b> to explore how fast work gets done in your process, like "85% of work finishes in 25 days or less."'
                values={{ ...boldText }}
              />
            </Typography>
            <Typography variant="body2" color="textPrimary">
              <FormattedMessage
                id="landing.page.features.overview.learn.cumulative.flow.diagram"
                defaultMessage="The <b>Cumulative Flow Diagram</b> shows how work moves through your system, helping you spot flow issues and opportunities to improve."
                values={{ ...boldText }}
              />
            </Typography>
            <Typography variant="body2" color="textPrimary">
              <FormattedMessage
                id="landing.page.features.overview.learn.punchline"
                defaultMessage="<b>And that’s just the beginning.</b>"
                values={{ ...boldText }}
              />
            </Typography>
          </Stack>
        </FeaturesHighlightColumn>
        <FeaturesHighlightColumn>
          <Stack spacing={2}>
            <FeaturesHighlightImage src={agingWIP} alt="Aging Work In Progress" loading="lazy" />
            <FeaturesHighlightTitle variant="subtitle1" color="textPrimary">
              <FormattedMessage
                id="landing.page.features.overview.manage.title"
                defaultMessage="Manage the present"
              />
            </FeaturesHighlightTitle>
            <Typography variant="body2" color="textPrimary">
              <FormattedMessage
                id="landing.page.features.overview.manage.body"
                defaultMessage="Use the <b>Aging Work in Progress Chart</b> to see how
                long current work has been in progress. Compare each item to past
                performance using percentile lines pulled from the <b>Cycle Time
                Scatterplot</b> to spot work at risk of late delivery. Highlight
                stalled items and catch early signs of slow progress so you can
                intervene and keep flow predictable."
                values={{ ...boldText }}
              />
            </Typography>
          </Stack>
        </FeaturesHighlightColumn>
        <FeaturesHighlightColumn>
          <Stack spacing={2}>
            <FeaturesHighlightImage
              src={isDarkTheme ? monteCarloWhenDark : monteCarloWhenLight}
              alt="Monte Carlo When"
              loading="lazy"
            />
            <FeaturesHighlightTitle variant="subtitle1" color="textPrimary">
              <FormattedMessage
                id="landing.page.features.overview.forecast.title"
                defaultMessage="Forecast the future"
              />
            </FeaturesHighlightTitle>
            <Typography variant="body2" color="textPrimary">
              <FormattedMessage
                id="landing.page.features.overview.forecast.cycle.time.scatterplot"
                defaultMessage="Use the <b>Cycle Time Scatterplot</b> to estimate how long a single
                item might take, using real percentile data."
                values={{ ...boldText }}
              />
            </Typography>
            <Typography variant="body2" color="textPrimary">
              <FormattedMessage
                id="landing.page.features.overview.forecast.monte.carlo"
                defaultMessage="For larger efforts, turn to <b>Monte Carlo Simulations</b>,
                whether you’re asking <b>when</b> a fixed scope might finish or <b>how much</b>
                can be done by a given date, you’ll get answers grounded in reality."
                values={{ ...boldText }}
              />
            </Typography>
          </Stack>
        </FeaturesHighlightColumn>
      </FeaturesHighlights>
    </FeaturesOverviewSection>
  );
}
